import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChefHat, Utensils, CalendarCheck, Building2, Star, Quote, ShieldCheck } from "lucide-react";
import { QuoteButton } from "@/components/QuoteButton";
import { MenuButton } from "@/components/MenuButton";
import { StatsSection } from "@/components/StatsSection";
import { HalalSection } from "@/components/HalalSection";
import { useContent, useSiteImage } from "@/context/ContentContext";
import { useReveal, Seo } from "@/lib/seo";
import { api, mediaUrl } from "@/lib/api";
import { AMBIANCE_KEYS, GALLERY_CATEGORIES } from "@/lib/data";

const EXPERTISES = [
  {
    icon: Utensils,
    title: "Traiteur événementiel",
    text: "Mariages, cérémonies, événements privés, réceptions professionnelles, cocktails, buffets et prestations culinaires adaptées au projet.",
    cta: "Découvrir notre service traiteur",
    to: "/traiteur",
    imgKey: "home_traiteur",
  },
  {
    icon: ChefHat,
    title: "Cheffe à domicile",
    text: "Dîners en couple, repas entre amis, occasions spéciales et expériences culinaires personnalisées dans un cadre intime ou premium.",
    cta: "Organiser mon dîner privé",
    to: "/cheffe-a-domicile",
    imgKey: "home_chef",
  },
  {
    icon: CalendarCheck,
    title: "Coordination événementielle",
    text: "Préparation, organisation, coordination du jour J, gestion des prestataires, respect du planning et suivi opérationnel sur le terrain.",
    cta: "Présenter mon projet",
    to: "/coordination",
    imgKey: "home_coordination",
  },
];

export default function Home() {
  useReveal();
  const { content } = useContent();
  const img = useSiteImage();
  const [testimonials, setTestimonials] = useState([]);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const hero = content?.hero || {};
  const stats = content?.stats || [];

  return (
    <div data-testid="home-page">
      <Seo
        title="Afro Regal — Traiteur halal africain, antillais, français & afro-caribéen"
        description="Traiteur africain halal, traiteur antillais halal, traiteur français halal et afro-caribéen halal pour mariages, réceptions et entreprises dans l'Oise, en Île-de-France et Hauts-de-France. Cheffe à domicile et coordination événementielle. Option non halal sur demande. Demandez votre devis."
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-28 lg:pt-32" data-testid="home-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 pb-16 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:pb-24">
          <div className="fade-up lg:col-span-5">
            <p className="mb-6 text-sm uppercase tracking-[0.3em] text-[#9A7B2C]">Créateurs de beaux moments</p>
            <h1 className="font-serif text-5xl font-500 leading-[0.98] text-[#2C2C2C] sm:text-6xl lg:text-7xl">
              {hero.title || "L'art de recevoir. L'exigence du détail."}
            </h1>
            <p className="mt-7 max-w-md text-base leading-relaxed text-[#2C2C2C]/70 lg:text-lg">
              {hero.subtitle ||
                "Traiteur événementiel, cheffe à domicile et coordination événementielle : nous donnons vie à vos projets avec savoir-faire, élégance et sens de l'organisation."}
            </p>
            <p className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#C9A84C] bg-white px-4 py-2 text-sm font-medium text-[#2C2C2C]" data-testid="hero-halal-badge">
              <ShieldCheck size={16} className="text-[#C9A84C]" /> Traiteur africain, antillais, français & afro-caribéen halal
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <QuoteButton testId="hero-quote-btn" />
              <Link
                to="/realisations"
                data-testid="hero-realisations-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[#2C2C2C]/30 px-7 py-3 text-sm font-medium text-[#2C2C2C] transition-colors duration-300 hover:border-[#2C2C2C] hover:bg-[#2C2C2C] hover:text-white"
              >
                Découvrir nos réalisations <ArrowRight size={16} />
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap gap-x-6 gap-y-3 border-t border-[#2C2C2C]/10 pt-6" data-testid="hero-services">
              {EXPERTISES.map((e) => (
                <Link key={e.to} to={e.to} className="inline-flex items-center gap-2 text-sm text-[#2C2C2C]/80 transition-colors hover:text-[#9A7B2C]">
                  <e.icon size={16} strokeWidth={1.5} className="text-[#C9A84C]" /> {e.title}
                </Link>
              ))}
            </div>
          </div>
          <div className="relative lg:col-span-7">
            <div className="absolute -right-10 -top-10 hidden h-[110%] w-2/3 rounded-l-[48px] bg-[#C9A84C]/15 lg:block" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-[32px] shadow-[0_40px_80px_-40px_rgba(44,44,44,0.55)]">
              <img src={img("home_hero")} alt="Réception élégante : buffet raffiné, tables dressées et convives" className="aspect-[4/3] w-full object-cover lg:aspect-[5/4]" />
            </div>
            <div className="absolute -bottom-6 left-6 rounded-2xl bg-[#2C2C2C] px-6 py-5 text-white shadow-xl sm:left-10">
              <p className="font-serif text-2xl text-[#C9A84C]">Mariages · Réceptions · Entreprises</p>
              <p className="mt-1 text-xs uppercase tracking-[0.2em] text-white/70">Oise · Île-de-France · Hauts-de-France</p>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <StatsSection stats={stats} />

      {/* HALAL */}
      <HalalSection variant="light" testId="home-halal" />

      {/* EXPERTISES */}
      <section className="py-24 lg:py-32" data-testid="expertises-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="reveal mb-16 max-w-2xl">
            <p className="mb-3 text-sm uppercase tracking-[0.25em] text-[#C9A84C]">Nos expertises</p>
            <h2 className="font-serif text-3xl font-500 text-[#2C2C2C] lg:text-5xl">
              Trois savoir-faire, une même exigence
            </h2>
          </div>
          <div className="grid gap-6 lg:grid-cols-2 lg:grid-rows-2">
            {EXPERTISES.map((e, i) => (
              <div
                key={e.title}
                className={`reveal group relative overflow-hidden rounded-[28px] transition-shadow duration-300 hover:shadow-2xl ${i === 0 ? "min-h-[460px] lg:row-span-2 lg:min-h-[620px]" : "min-h-[300px]"}`}
                style={{ transitionDelay: `${i * 100}ms` }}
                data-testid={`expertise-${i}`}
              >
                <img src={img(e.imgKey)} alt={e.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2C]/90 via-[#2C2C2C]/35 to-transparent" />
                <div className="relative flex h-full flex-col justify-end p-7 lg:p-9">
                  <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-full border border-[#C9A84C] text-[#C9A84C]"><e.icon size={19} strokeWidth={1.5} /></span>
                  <h3 className={`font-serif text-white ${i === 0 ? "text-4xl" : "text-3xl"}`}>{e.title}</h3>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-white/80">{e.text}</p>
                  <Link to={e.to} data-testid={`expertise-link-${i}`} className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#C9A84C] transition-colors hover:text-white">
                    {e.cta} <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div className="reveal mt-12 flex flex-wrap justify-center gap-4">
            <MenuButton variant="outline" testId="home-expertises-carte">Découvrir notre carte</MenuButton>
            <Link to="/mariages" data-testid="home-mariages-link" className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-medium text-[#2C2C2C] transition-colors hover:text-[#9A7B2C]">
              Vous préparez un mariage ? <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* REALISATIONS PREVIEW */}
      <section className="bg-white py-24 lg:py-32" data-testid="realisations-preview">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="reveal mb-14 flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-xl">
              <p className="mb-3 text-sm uppercase tracking-[0.25em] text-[#C9A84C]">Nos réalisations</p>
              <h2 className="font-serif text-3xl font-500 text-[#2C2C2C] lg:text-5xl">Une sélection de moments créés</h2>
            </div>
            <Link to="/realisations" data-testid="see-all-realisations" className="inline-flex items-center gap-2 text-sm font-medium text-[#2C2C2C] hover:text-[#C9A84C]">
              Voir toutes nos réalisations <ArrowRight size={15} />
            </Link>
          </div>
          <div className="grid auto-rows-[180px] grid-cols-2 gap-4 lg:auto-rows-[220px] lg:grid-cols-4">
            {(gallery.length ? gallery.map((g) => mediaUrl(g.url)) : AMBIANCE_KEYS.concat(AMBIANCE_KEYS).slice(0, 6).map(img)).map((src, i) => (
              <div key={i} className={`reveal group relative overflow-hidden rounded-xl ${i === 0 ? "col-span-2 row-span-2" : i === 3 ? "lg:col-span-2" : ""}`} style={{ transitionDelay: `${i * 60}ms` }}>
                <img src={src} alt="Réalisation Afro Regal" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROFESSIONNELS */}
      <section className="bg-[#2C2C2C] py-24 lg:py-32" data-testid="pro-section">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-12 lg:px-8">
          <div className="reveal lg:col-span-6 lg:order-2">
            <div className="overflow-hidden rounded-[28px] border border-[#C9A84C]/30 p-2">
              <img src={img("home_pro")} alt="Service traiteur pour les professionnels" className="aspect-[4/3] w-full rounded-[22px] object-cover" />
            </div>
          </div>
          <div className="reveal lg:col-span-6 lg:order-1">
            <div className="mb-3 inline-flex items-center gap-2 text-sm uppercase tracking-[0.25em] text-[#C9A84C]">
              <Building2 size={16} /> Pour les professionnels
            </div>
            <h2 className="font-serif text-3xl font-500 text-white lg:text-5xl">
              Entreprises, institutions, artistes et productions
            </h2>
            <p className="mt-5 text-base leading-relaxed text-white/70">
              Cocktails, inaugurations, séminaires, conférences, événements institutionnels, tournages, spectacles et besoins de catering : nous accompagnons les entreprises, institutions, associations, clubs, artistes et équipes de production.
            </p>
            <div className="mt-8">
              <QuoteButton testId="pro-quote-btn" variant="gold" service="Entreprise ou institution">Demander un devis entreprise</QuoteButton>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="bg-white py-24 lg:py-32" data-testid="testimonials-section">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <div className="reveal mb-14 max-w-2xl">
              <p className="mb-3 text-sm uppercase tracking-[0.25em] text-[#9A7B2C]">Témoignages</p>
              <h2 className="font-serif text-3xl font-500 text-[#2C2C2C] lg:text-5xl">Ce que disent nos clients</h2>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {testimonials.slice(0, 6).map((t) => (
                <div key={t.id} className="reveal rounded-2xl border border-[#C9A84C]/30 bg-[#FAF6EF] p-7" data-testid={`testimonial-${t.id}`}>
                  <Quote size={24} className="text-[#C9A84C]" />
                  {t.rating ? (
                    <div className="mt-3 flex gap-0.5">
                      {Array.from({ length: Math.round(t.rating) }).map((_, i) => (
                        <Star key={i} size={14} className="fill-[#C9A84C] text-[#C9A84C]" />
                      ))}
                    </div>
                  ) : null}
                  <p className="mt-4 text-sm leading-relaxed text-[#2C2C2C]/80">“{t.text}”</p>
                  <p className="mt-5 text-sm font-medium text-[#2C2C2C]">{t.author}</p>
                  <p className="text-xs text-[#2C2C2C]/50">Source : {t.source}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="px-5 py-24 lg:px-8" data-testid="final-cta">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-8 rounded-[32px] bg-[#C9A84C] px-8 py-14 lg:flex-row lg:items-center lg:px-16">
          <div className="max-w-xl">
            <h2 className="font-serif text-3xl font-500 text-[#2C2C2C] lg:text-5xl">Parlons de votre prochain événement.</h2>
            <p className="mt-4 text-base leading-relaxed text-[#2C2C2C]/80">
              Chaque projet est différent. Présentez-nous votre événement pour recevoir une proposition adaptée à vos besoins.
            </p>
          </div>
          <div className="shrink-0">
            <QuoteButton testId="final-cta-quote-btn" />
          </div>
        </div>
      </section>
    </div>
  );
}
